import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useState } from 'react';

import type { PersonaSummary } from '../services/api';

interface PersonaSelectorProps {
  personas: PersonaSummary[];
  personasLoading?: boolean;
  selectedPersonaId: string;
  customPersona: string;
  personaRoleContext: string;
  onSelectedPersonaIdChange: (value: string) => void;
  onCustomPersonaChange: (value: string) => void;
  onPersonaRoleContextChange: (value: string) => void;
  title?: string;
  helperText?: string;
}

export default function PersonaSelector({
  personas,
  personasLoading = false,
  selectedPersonaId,
  customPersona,
  personaRoleContext,
  onSelectedPersonaIdChange,
  onCustomPersonaChange,
  onPersonaRoleContextChange,
  title = 'Target Persona (Optional)',
  helperText = 'Select a persona from PersonaFab.com or enter a custom description.',
}: PersonaSelectorProps) {
  // Default to the Custom tab only when a custom description is present and no
  // PersonaFab persona is selected; otherwise show the PersonaFab dropdown.
  const [tab, setTab] = useState<number>(customPersona && !selectedPersonaId ? 1 : 0);

  return (
    <Box sx={{ mb: 2, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <PersonIcon fontSize="small" color="primary" />
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {helperText}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          mb: 2,
          minHeight: 36,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none' },
        }}
      >
        <Tab label="PersonaFab Persona" />
        <Tab label="Custom Persona" />
      </Tabs>

      {tab === 0 ? (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="persona-select-label">Select from PersonaFab.com</InputLabel>
            <Select
              labelId="persona-select-label"
              id="persona-select"
              value={selectedPersonaId}
              onChange={(e) => {
                const personaId = e.target.value as string;
                onSelectedPersonaIdChange(personaId);

                if (personaId) {
                  onCustomPersonaChange('');
                  let selectedPersona = personas.find((p) => p.id === personaId);
                  if (!selectedPersona && personaId.startsWith('idx-')) {
                    const idx = parseInt(personaId.replace('idx-', ''), 10);
                    selectedPersona = personas[idx];
                  }
                  if (selectedPersona) {
                    const parts: string[] = [];
                    if (selectedPersona.personaName) parts.push(`Persona: ${selectedPersona.personaName}`);
                    if (selectedPersona.userRole) parts.push(`Role: ${selectedPersona.userRole}`);
                    if (selectedPersona.useCase) parts.push(`Use Case: ${selectedPersona.useCase}`);
                    if (selectedPersona.description) parts.push(`Description: ${selectedPersona.description}`);
                    onPersonaRoleContextChange(parts.join('\n'));
                  }
                } else {
                  onPersonaRoleContextChange('');
                }
              }}
              label="Select from PersonaFab.com"
              disabled={personasLoading}
            >
              <MenuItem key="none" value="">
                <em>None</em>
              </MenuItem>
              {personas.map((persona, idx) => (
                <MenuItem key={persona.id || `persona-${idx}`} value={persona.id || `idx-${idx}`}>
                  {persona.personaName || 'Unnamed Persona'}
                  {persona.userRole && ` — ${persona.userRole}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedPersonaId && personaRoleContext && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', p: 1, bgcolor: 'action.hover', borderRadius: 0.5, whiteSpace: 'pre-wrap' }}
            >
              {personaRoleContext}
            </Typography>
          )}
        </>
      ) : (
        <TextField
          fullWidth
          multiline
          minRows={6}
          size="small"
          label="Custom Persona Description"
          placeholder="e.g., Senior software engineer with 10 years experience building distributed systems; values concise, technical answers."
          value={customPersona || ''}
          onChange={(e) => {
            const value = e.target.value;
            onCustomPersonaChange(value);
            if (value) {
              onSelectedPersonaIdChange('');
              onPersonaRoleContextChange(value);
            } else {
              onPersonaRoleContextChange('');
            }
          }}
        />
      )}
    </Box>
  );
}
